// src/ApiPanel/pages/gst/GstReversalReport.jsx
// ITC reversals, grouped by the rule they were reversed under — which is the
// thing an auditor asks about first, so it gets its own summary rather than
// being buried in a column.
import React, { useMemo, useState } from "react";
import {
  Badge, Card, DataTable, ExportButton, FilterBar, Modal, MonthField, Notice,
  Pagination, ReportPage, SelectField, Stat, StatGrid, TextField, count,
  money, prettyDate, rupees, todayISO,
} from "../../components/reports/ReportKit";
import { useAction, useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";
import { useApiPanelSession } from "../../context/ApiPanelSessionContext";

const RULES = [
  { value: "RULE_37", label: "Rule 37 — not paid in 180 days" },
  { value: "RULE_42", label: "Rule 42 — inputs partly exempt/personal" },
  { value: "RULE_43", label: "Rule 43 — capital goods partly exempt" },
  { value: "SEC_17_5", label: "Section 17(5) — blocked credit" },
  { value: "CREDIT_NOTE", label: "Supplier credit note" },
  { value: "OTHER", label: "Other" },
];

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "APPROVED", label: "Approved" },
  { value: "REVERSED", label: "Reversed in return" },
];

const currentMonth = () => new Date().toISOString().slice(0, 7);
const defaults = () => ({ period: "", gstin: "", rule: "", rev_status: "", page: 1 });

export default function GstReversalReport() {
  const { user } = useApiPanelSession();
  const [filters, setFilters] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const [exporting, setExporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const { data, loading, error, reload } = useReportQuery(
    API_PANEL_ENDPOINTS.GST_REVERSAL_REPORT,
    applied
  );
  const action = useAction();

  const totals = data?.totals || {};
  const rows = data?.data || [];
  const byRule = data?.by_rule || [];
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${API_PANEL_ENDPOINTS.GST_REVERSAL_REPORT}${qs({ ...applied, export: "csv", page: undefined })}`,
        "gst_reversal.csv"
      );
    } catch {
      /* list unaffected */
    } finally {
      setExporting(false);
    }
  };

  const approve = async (row) => {
    if (!window.confirm(`Approve the ${row.rule} reversal of ${rupees(row.total)}?`)) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.GST_REVERSAL_ACTION,
      { action: "approve", id: row.id },
      { form: true }
    );
    if (res) reload();
  };

  return (
    <ReportPage
      title="GST Reversal Report"
      subtitle="Input tax credit reversed, and the rule each reversal was made under."
      actions={
        <>
          <button type="button" className="apip-btn apip-btn-primary" onClick={() => setShowAdd(true)}>
            <i className="iconoir-plus" /> Add Reversal
          </button>
          <ExportButton onExport={handleExport} busy={exporting} />
        </>
      }
    >
      <Card>
        <FilterBar
          onApply={() => setApplied({ ...filters, page: 1 })}
          onReset={() => {
            const fresh = defaults();
            setFilters(fresh);
            setApplied(fresh);
          }}
          busy={loading}
        >
          <MonthField label="Tax period" value={filters.period} onChange={set("period")} max={currentMonth()} />
          <TextField label="GSTIN" value={filters.gstin} onChange={(v) => set("gstin")(v.toUpperCase())} maxLength={15} />
          <SelectField label="Rule" value={filters.rule} onChange={set("rule")} options={RULES} />
          <SelectField label="Status" value={filters.rev_status} onChange={set("rev_status")} options={STATUSES} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Entries" value={count(totals.entries)} />
        <Stat label="CGST" value={rupees(totals.cgst)} />
        <Stat label="SGST" value={rupees(totals.sgst)} />
        <Stat label="IGST" value={rupees(totals.igst)} />
        <Stat label="Total Reversed" value={rupees(totals.total)} tone="warn" />
      </StatGrid>

      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      {byRule.length > 0 && (
        <Card title="By rule">
          <div className="apip-rule-grid">
            {byRule.map((r) => (
              <div className="apip-rule-tile" key={r.rule}>
                <span className="apip-rule-code">{r.rule}</span>
                <span className="apip-rule-label">{r.rule_label}</span>
                <span className="apip-rule-total">{rupees(r.total)}</span>
                <span className="apip-rule-count">{count(r.count)} entries</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card title="Reversals">
        <DataTable
          columns={[
            { key: "period", label: "Period" },
            { key: "gstin", label: "GSTIN", render: (r) => <span className="apip-mono">{r.gstin}</span> },
            {
              key: "rule",
              label: "Rule",
              render: (r) => (
                <span className="apip-party">
                  {r.rule}
                  <small>{r.rule_label}</small>
                </span>
              ),
            },
            { key: "reason", label: "Reason" },
            {
              key: "supplier_invoice_no",
              label: "Supplier Invoice",
              render: (r) =>
                r.supplier_invoice_no ? (
                  <span className="apip-party">
                    <span className="apip-mono">{r.supplier_invoice_no}</span>
                    <small>{prettyDate(r.supplier_invoice_date)}</small>
                  </span>
                ) : (
                  "—"
                ),
            },
            { key: "cgst", label: "CGST", numeric: true, render: (r) => money(r.cgst) },
            { key: "sgst", label: "SGST", numeric: true, render: (r) => money(r.sgst) },
            { key: "igst", label: "IGST", numeric: true, render: (r) => money(r.igst) },
            { key: "total", label: "Total", numeric: true, render: (r) => <strong>{money(r.total)}</strong> },
            { key: "status", label: "Status", render: (r) => <Badge value={r.status} /> },
            {
              key: "actions",
              label: "",
              width: 130,
              render: (r) => {
                if (r.status !== "DRAFT") return null;
                if (r.created_by && user?.name && r.created_by === user.name) {
                  return <span className="apip-hint">Awaiting another approver</span>;
                }
                return (
                  <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost" disabled={action.busy} onClick={() => approve(r)}>
                    Approve
                  </button>
                );
              },
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty="No reversals recorded for these filters."
          rowKey={(r) => r.id}
        />
        <Pagination meta={data?.pagination} onPage={(page) => setApplied((a) => ({ ...a, page }))} />
      </Card>

      <AddReversalModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onDone={() => {
          setShowAdd(false);
          reload();
        }}
      />
    </ReportPage>
  );
}

function AddReversalModal({ open, onClose, onDone }) {
  const action = useAction();
  const [form, setForm] = useState({
    gstin: "", period: currentMonth(), rule: "RULE_37", reason: "",
    supplier_gstin: "", supplier_invoice_no: "", supplier_invoice_date: "",
    cgst: "", sgst: "", igst: "", cess: "",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const total = useMemo(
    () => ["cgst", "sgst", "igst", "cess"].reduce((acc, h) => acc + (Number(form[h]) || 0), 0),
    [form]
  );

  const submit = async (e) => {
    e.preventDefault();
    const res = await action.run(
      API_PANEL_ENDPOINTS.GST_REVERSAL_ACTION,
      { action: "create", ...form },
      { form: true }
    );
    if (res) onDone();
  };

  return (
    <Modal
      open={open}
      title="Add an ITC reversal"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" form="apip-rev-form" className="apip-btn apip-btn-primary" disabled={action.busy || total <= 0}>
            {action.busy ? "Saving…" : "Save as Draft"}
          </button>
        </>
      }
    >
      <form id="apip-rev-form" onSubmit={submit}>
        <Notice tone="info">
          Saved as <strong>Draft</strong>. A second person approves it before it counts against
          the period&rsquo;s credit. A period that is already locked or filed will refuse the entry.
        </Notice>

        <div className="apip-form-grid">
          <label className="apip-field">
            <span>Your GSTIN</span>
            <input type="text" className="apip-input apip-mono" required maxLength={15} value={form.gstin}
              onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))} />
          </label>
          <label className="apip-field">
            <span>Tax period</span>
            <input type="month" className="apip-input" required max={currentMonth()} value={form.period} onChange={set("period")} />
          </label>
          <label className="apip-field apip-field-wide">
            <span>Rule</span>
            <select className="apip-input" required value={form.rule} onChange={set("rule")}>
              {RULES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        </div>

        <label className="apip-field">
          <span>Reason</span>
          <textarea className="apip-input" rows={2} required minLength={5} maxLength={250} value={form.reason} onChange={set("reason")}
            placeholder="Why this credit is being reversed" />
        </label>

        <h4 className="apip-form-subhead">Supplier document (optional)</h4>
        <div className="apip-form-grid">
          <label className="apip-field">
            <span>Supplier GSTIN</span>
            <input type="text" className="apip-input apip-mono" maxLength={15} value={form.supplier_gstin}
              onChange={(e) => setForm((f) => ({ ...f, supplier_gstin: e.target.value.toUpperCase() }))} />
          </label>
          <label className="apip-field">
            <span>Invoice no</span>
            <input type="text" className="apip-input apip-mono" maxLength={32} value={form.supplier_invoice_no} onChange={set("supplier_invoice_no")} />
          </label>
          <label className="apip-field">
            <span>Invoice date</span>
            <input type="date" className="apip-input" max={todayISO()} value={form.supplier_invoice_date} onChange={set("supplier_invoice_date")} />
          </label>
        </div>

        <h4 className="apip-form-subhead">Amount reversed</h4>
        <div className="apip-form-grid apip-form-grid-4">
          {["cgst", "sgst", "igst", "cess"].map((head) => (
            <label className="apip-field" key={head}>
              <span>{head.toUpperCase()}</span>
              <input type="number" className="apip-input" min="0" step="0.01" value={form[head]} onChange={set(head)} placeholder="0.00" />
            </label>
          ))}
        </div>

        <div className="apip-computed">
          <span>Total reversal</span>
          <strong>{rupees(total.toFixed(2))}</strong>
        </div>

        {action.error && <Notice tone="bad">{action.error}</Notice>}
      </form>
    </Modal>
  );
}
